// GlobalFooter.js - Unified Shadcn Footer7 Pattern Component
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { isEduPortal, isPathbuddyHost } from '../../config/eduHost';
import { withAppBasename } from '../../config/appBase';
import AppAnchor from '../AppAnchor/AppAnchor';
import PathbuddySiteFooter from '../../features/edu-landing/PathbuddySiteFooter';
import CookieSettingsButton from '../../features/consent/CookieSettingsButton';
import './GlobalFooter.css';

const GlobalFooter = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const onPathbuddy = isPathbuddyHost();

  // pathbuddy.com uses the same editorial-style footer as the marketing home.
  // edu.ivori.ai / other hosts keep this GlobalFooter layout.
  if (onPathbuddy) {
    return <PathbuddySiteFooter />;
  }

  const handleNav = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="global-footer">
      <div className="global-footer-container">

        {/* Main Grid: Left Brand Block + Nav Link Columns */}
        <div className="global-footer-main">

          {/* Left Brand Column */}
          {(() => {
            const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
            const isEduDomain =
              isEduPortal(hostname) ||
              hostname.includes('edu.') ||
              hostname.startsWith('edu');
            return (
              <div className="global-footer-brand">
                <div className="global-footer-brand-header">
                  <a href={withAppBasename('/')} onClick={(e) => { e.preventDefault(); handleNav('/'); }} className="global-footer-logo-link" aria-label="Ivori Home">
                    <img src="/logo-yellow.png" alt="Ivori" className="global-footer-logo" />
                    <span className="global-footer-brand-name">
                      IVORI
                      {isEduDomain && <span className="brand-sub-tag">Edu</span>}
                    </span>
                  </a>
                </div>
                <p className="global-footer-statement">
                  AI for decision-makers. Tuned for the ones about to become one.
                </p>
                <p className="global-footer-contact" style={{ marginTop: '22px', fontSize: '13.5px', color: 'var(--iv-muted)', lineHeight: 1.5 }}>
                  Questions? Email us:{' '}
                  <a href="mailto:support@ivoriai.com" style={{ color: 'var(--iv-body)', textDecoration: 'underline', fontWeight: 600 }}>
                    support@ivoriai.com
                  </a>
                </p>
              </div>
            );
          })()}

          {/* Right Columns Grid */}
          <div className="global-footer-grid">

            {/* Column 1: Company & Products */}
            <div className="global-footer-col">
              <h3 className="global-footer-col-title">Company</h3>
              <div className="global-footer-links" style={{ marginBottom: '22px' }}>
                <a
                  href="https://ivorilab.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="global-footer-link"
                >
                  ivori lab
                </a>
              </div>

              <h3 className="global-footer-col-title">Products</h3>
              <div className="global-footer-links">
                <a
                  href="https://ivoriai.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="global-footer-link"
                >
                  ivori edu
                </a>
              </div>

              <h3 className="global-footer-col-title" style={{ marginTop: '22px' }}>Follow</h3>
              <div className="global-footer-links">
                <a
                  href="https://www.linkedin.com/company/ivorilab/posts/?feedView=all"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="global-footer-link"
                >
                  IVORI Lab on LinkedIn
                </a>
                <a
                  href="https://www.linkedin.com/showcase/ivoriedu/posts/?feedView=all"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="global-footer-link"
                >
                  IVORI Edu on LinkedIn
                </a>
              </div>
            </div>

            {/* Column 2: Features */}
            <div className="global-footer-col">
              <h3 className="global-footer-col-title">Features</h3>
              <div className="global-footer-links">
                <AppAnchor href="/chat" className="global-footer-link">
                  Chat
                </AppAnchor>
                {/* No internal route exists for "Fluid" -- left as an
                    external link, flagging rather than guessing one. */}
                <a
                  href="https://ivoriai.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="global-footer-link"
                >
                  Fluid
                </a>
                <AppAnchor href="/studio" className="global-footer-link">
                  Studio
                </AppAnchor>
                <AppAnchor href="/apps" className="global-footer-link">
                  Apps
                </AppAnchor>

                <AppAnchor href="/cases" className="global-footer-link">
                  Cases
                </AppAnchor>
              </div>
            </div>

            {/* Column 3: Exam Apps */}
            <div className="global-footer-col">
              <h3 className="global-footer-col-title">Exam Apps</h3>
              <div className="global-footer-links">
                <a
                  href="https://ivoriai.com/edu/esat"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="global-footer-link"
                >
                  ESAT
                </a>
                <a
                  href="https://ivoriai.com/edu/lnat"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="global-footer-link"
                >
                  LNAT
                </a>
                <a
                  href="https://ivoriai.com/edu/tara"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="global-footer-link"
                >
                  TARA
                </a>
                <a
                  href="https://ivoriai.com/edu/tmua"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="global-footer-link"
                >
                  TMUA
                </a>
                <a
                  href="https://ivoriai.com/edu/ucat"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="global-footer-link"
                >
                  UCAT
                </a>
              </div>
            </div>

            {/* Column 4: Interview Apps */}
            <div className="global-footer-col">
              <h3 className="global-footer-col-title">Interview Apps</h3>
              <div className="global-footer-links">
                <a
                  href="https://ivoriai.com/edu/oxbridge"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="global-footer-link"
                >
                  Oxbridge
                </a>
                <a
                  href="https://ivoriai.com/edu/medicine"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="global-footer-link"
                >
                  Medical School
                </a>
                <a
                  href="https://ivoriai.com/edu/degree-apprenticeships"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="global-footer-link"
                >
                  Degree Apprenticeship
                </a>
              </div>
            </div>

          </div>
        </div>

        {/* Bottom Bar: Copyright Left + Legal Links Right */}
        <div className="global-footer-bottom">
          <p className="global-footer-copyright">
            © {currentYear}{' '}
            <a
              href="https://ivorilab.com"
              target="_blank"
              rel="noopener noreferrer"
              className="global-footer-copyright-link"
            >
              ivori lab
            </a>
            . all rights reserved.
          </p>

          <ul className="global-footer-legal-links">
            <li>
              <a
                href={withAppBasename('/privacy')}
                onClick={(e) => { e.preventDefault(); handleNav('/privacy'); }}
                className="global-footer-legal-link"
              >
                Privacy
              </a>
            </li>
            <li>
              <a
                href={withAppBasename('/terms-of-use')}
                onClick={(e) => { e.preventDefault(); handleNav('/terms-of-use'); }}
                className="global-footer-legal-link"
              >
                Terms of Use
              </a>
            </li>
            <li>
              <a
                href={withAppBasename('/safeguarding')}
                onClick={(e) => { e.preventDefault(); handleNav('/safeguarding'); }}
                className="global-footer-legal-link"
              >
                Safeguarding
              </a>
            </li>
            <li>
              <a
                href={withAppBasename('/cookies')}
                onClick={(e) => { e.preventDefault(); handleNav('/cookies'); }}
                className="global-footer-legal-link"
              >
                Cookies
              </a>
            </li>
            <li>
              <CookieSettingsButton className="global-footer-legal-link" />
            </li>
          </ul>
        </div>

      </div>
    </footer>
  );
};

export default GlobalFooter;
